import { capitalize } from 'App/Helpers';
import { isIOS } from 'App/Helpers/deviceInfo';
import { getStorageValue } from 'App/Helpers/localStorageHandler';

/**
 * iPadOS 13+ sends a desktop Mac user agent, so `iPad` is absent and `isIOS`
 * misses it. Touch points on `MacIntel` are the remaining signal.
 *
 * @returns {boolean}
 */
const isIPadOS = () => navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

/**
 * Push is unsupported on Safari on every device, and on every browser on iOS
 * (iPhone, iPod, iPad, including iPadOS desktop mode and Chrome/Firefox/Edge).
 *
 * @returns {boolean}
 */
const isSafariOrIOS = () => {
    if (typeof navigator === 'undefined') return true;

    const ua = navigator.userAgent || '';

    if (isIOS() || isIPadOS()) return true;
    // iOS-only browser tokens, including when the device token was stripped.
    if (/iPad|iPhone|iPod|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return true;

    // Chromium UAs also contain "Safari". Apple's vendor covers desktop Safari
    // and every iOS browser, which is WebKit and reports "Apple Computer, Inc."
    return (
        (/Safari/i.test(ua) &&
            !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|SamsungBrowser|Android/i.test(ua)) ||
        /apple/i.test(navigator.vendor || '')
    );
};

/**
 * Detects if Web Push is available in the current browser/OS (sync, no async).
 * Uses feature detection that works across browsers and OS (Chrome, Firefox,
 * Edge, Samsung Internet, Android, etc.).
 * - Requires secure context (HTTPS or localhost).
 * - Requires Notification API and Service Worker support (both needed for Web Push).
 * - Returns false for Safari on any device, and for any browser on iOS.
 *
 * @returns {boolean} - `true` if Web Push is supported and usable, otherwise `false`.
 */
export const isWebPushAvailable = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }
    if (!window.isSecureContext) {
        return false;
    }
    if (!('Notification' in window)) {
        return false;
    }
    if (!('serviceWorker' in navigator)) {
        return false;
    }

    return !isSafariOrIOS();
};

/**
 * Returns whether prompting the user for push notifications is currently possible.
 *
 * @returns {boolean} - `true` if push notification prompt can be shown, otherwise `false`.
 */
export const canPromptForPushNotification = () =>
    isWebPushAvailable() && Notification.permission === 'default';

export function getPushDimension(pushStatus) {
    if (pushStatus === 'default') {
        return 'push_default';
    }
    if (pushStatus === 'granted') {
        return 'push_granted';
    }
    if (pushStatus === 'denied' || pushStatus === 'notAvailable') {
        return 'push_denied';
    }

    return undefined;
}

/**
 * Resolves the push dimension to seed when GameAnalytics initializes.
 *
 * Only `called` needs handling here, and never on a fresh load:
 * {@link getPushStatus} normalises a stored `called` back to `default`, and
 * nothing can open a prompt before consent is answered. It reaches init on a
 * re-init - withdrawing and re-granting analytics consent rebuilds the client
 * - while a native prompt the user ignored rather than dismissed still has
 * `called` pending, since `requestPushPermission` never settles.
 *
 * Seeding the `undefined` that {@link getPushDimension} returns for that state
 * is not harmless: the SDK treats an empty value as "keep what you have" and
 * restores the dimension persisted by the previous visit. A prompt being open
 * means the permission is still `default`.
 *
 * @param {string} pushStatus - Current push status.
 * @returns {string} A value from `GA_DIMENSION_PUSH_PRESET`.
 */
export const getInitialPushDimension = (pushStatus) =>
    getPushDimension(pushStatus === 'called' ? 'default' : pushStatus);

/**
 * Applies functional consent to the stored push status.
 *
 * Runs while rendering rather than from an effect. `PushProvider` sits above
 * the consumers that react to consent, and React flushes effects child-first,
 * so an effect here always resolved one render *after* a consumer had already
 * acted on the unreconciled status - which is how GameAnalytics ended up
 * seeding a push dimension it then had to correct.
 *
 * Undecided consent is not handled here - the stored value is what we know so
 * far, and `pushStatusReady` is what tells consumers not to act on it yet.
 *
 * @param {Object} params
 * @param {string} params.storedStatus - Status held in state / storage.
 * @param {boolean} params.isPushAvailable - Whether Web Push is usable here.
 * @param {boolean|undefined} params.functionalConsent - Undecided while undefined.
 * @returns {('default'|'called'|'granted'|'denied'|'notAvailable')}
 */
export const resolvePushStatus = ({ storedStatus, isPushAvailable, functionalConsent }) => {
    if (!isPushAvailable) return 'notAvailable';
    if (functionalConsent === false) return 'denied';

    return storedStatus;
};

/**
 * Checks if the push phase may still be shown, based on the persisted decision.
 *
 * The value is persisted so users are not repeatedly prompted across sessions.
 *
 * @param {string} storageKey - The base key/namespace used by storage helpers.
 * @return boolean
 */
export const canShowPushPhase = (storageKey) => {
    const showPhase = getStorageValue(storageKey, 'push.showPhase');

    return showPhase !== false;
};

/**
 * Resolves the current push notification permission status.
 *
 * Possible return values:
 * - 'default'        – permission not yet requested
 * - 'called'         – permission request has been triggered
 * - 'granted'        – permission granted by the user
 * - 'denied'         – permission denied by the user
 * - 'notAvailable'   – push notifications not supported
 *
 * The persisted value is the user's own answer to our prompt and outranks the
 * browser permission, so a decision made here stands even if the permission is
 * later changed in site settings. The browser is consulted only when nothing
 * has been recorded yet, which is how a silent grant on PWA install is picked
 * up. A stored `called` never survives a load - a prompt cannot still be open.
 *
 * @param {string} storageKey - The base key/namespace used by storage helpers.
 * @return {('default'|'called'|'granted'|'denied'|'notAvailable')}
 */
export const getPushStatus = (storageKey) => {
    const status = getStorageValue(storageKey, 'push.status');

    if (status === 'granted' || status === 'denied') return status;
    if (!status) return Notification?.permission;

    return 'default';
};

const PUSH_GA_CALL_SOURCE = {
    lp: 'LP',
    sidebar: 'Sidebar',
    game: 'Game',
};

/**
 * Sends GameAnalytics / GA events for the native push-permission result.
 *
 * Event names keep the original labels: `LP`, `Sidebar`, `Game:${gameScreenType}`.
 *
 * @param {Object} params
 * @param {'lp'|'sidebar'|'game'} params.callSource - Where the prompt was triggered.
 * @param {string} [params.gameScreenType] - Unity screen name, only for `callSource: 'game'`.
 * @param {NotificationPermission} params.permission - Result from `requestPushPermission`.
 * @param {Function} params.sendGameDesignEvent - Deferred GameAnalytics design event.
 * @param {Function} params.sendGoogleBasicEvent - Deferred Google Analytics basic event.
 */
export const sendPushGA = ({
    callSource,
    gameScreenType,
    permission,
    sendGameDesignEvent,
    sendGoogleBasicEvent,
}) => {
    const sourceLabel = PUSH_GA_CALL_SOURCE[callSource];
    const screenType = callSource === 'game' ? `${sourceLabel}:${gameScreenType}` : sourceLabel;

    sendGameDesignEvent(`Push:${capitalize(permission)}:${screenType}`);
    sendGoogleBasicEvent('push', `${permission}_${screenType.toLowerCase()}`);
};
